import { GooglePlaceLike } from "../interfaces/GooglePlace";

// Google address_components types that carry the municipality name,
// in preference order
const CITY_COMPONENT_TYPES = ['locality', 'postal_town', 'administrative_area_level_3'];

/**
 * Extract the city (municipality) name from a Google Maps PlaceResult or
 * GeocoderResult. Returns undefined when the value is not a place object
 * (searchParameters.location can also be a plain string or a latlng array)
 * or carries no recognizable city component.
 */
export function extractCityFromPlace(place?: GooglePlaceLike | string | unknown[] | null): string | undefined {
    if (!place || typeof place !== 'object' || Array.isArray(place)) {
        return undefined;
    }

    const components = (place as GooglePlaceLike).address_components;
    if (!components?.length) {
        return undefined;
    }

    for (const type of CITY_COMPONENT_TYPES) {
        const match = components.find((component) => component.types?.includes(type));
        if (match?.long_name) {
            return match.long_name;
        }
    }

    return undefined;
}

/**
 * Extract the postal code from a Google Maps PlaceResult / GeocoderResult when
 * present. City-level searches (locality) carry none, so this returns undefined
 * then — callers fall back to the city. Only area-level location is ever sent to
 * analytics (no street address).
 */
export function extractPostalFromPlace(place?: GooglePlaceLike | string | unknown[] | null): string | undefined {
    if (!place || typeof place !== 'object' || Array.isArray(place)) {
        return undefined;
    }

    const components = (place as GooglePlaceLike).address_components;
    if (!components?.length) {
        return undefined;
    }

    const match = components.find((component) => component.types?.includes('postal_code'));
    return match?.long_name ?? match?.short_name ?? undefined;
}
